<script setup>
import { onMounted, ref, computed } from 'vue'
import { useWebSiteStore } from '@/stores/website.store'
import { useNotify } from '@/composables/notify'
import { getUpdatePaymentMethodTransactionAsync, previewResumeSubscriptionAsync, resumeSubscriptionAsync } from '@/apis/subscriptions.api'
import { usePaddle } from '@/composables/paddle'
import i18n from '@/plugins/i18n'
import { useRouter } from 'vue-router'

// --- 1. Init & Composables ---
const t = i18n.global.t
const { notifyError } = useNotify()
const websiteStore = useWebSiteStore()
const { init, isReady, openCheckout } = usePaddle()
const router = useRouter()
// --- 2. State & Computed ---
const isLoadingAction = ref(false)
const showCheckout = ref(false)
const showPreviewDialog = ref(false)
const previewData = ref(null)

const pausedSubscription = computed(() => websiteStore.pausedSubscription)
const currency = computed(() => previewData.value?.recurring_transaction_details?.totals?.currency_code || 'EUR')

const formatCurrency = (amount) => {
  if (!amount) return '0,00'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: currency.value
  }).format(parseFloat(amount) / 100)
}

// --- 3. API Actions ---

const onUpdatePaymentMethod = async() => {
  if (!pausedSubscription.value) return
  if (!isReady.value) return

  isLoadingAction.value = true
  try {
    showCheckout.value = true
    const response = await getUpdatePaymentMethodTransactionAsync(pausedSubscription.value.id)
    const { transaction_id } = response.data
    openCheckout(transaction_id)
  } catch (error) {
    console.error('Error updating payment method:', error)
    notifyError(t('subscriptions.manage.updateCardFailed'))
    showCheckout.value = false
  } finally {
    isLoadingAction.value = false
  }
}

const onResumeSubscription = async() => {
  if (!pausedSubscription.value) return

  isLoadingAction.value = true
  try {
    const preview = await previewResumeSubscriptionAsync(pausedSubscription.value.id)
    previewData.value = preview.data.preview
    showPreviewDialog.value = true
  } catch (error) {
    notifyError(t('subscriptions.resume.previewFailed'))
  } finally {
    isLoadingAction.value = false
  }
}
// --- 4. Dialogs / Handlers ---
const handleConfirmResume = async() => {
  if (!pausedSubscription.value) return

  isLoadingAction.value = true
  try {
    await resumeSubscriptionAsync(pausedSubscription.value.id)
    // Refresh subscription data
    router.push({ name: 'subscriptions-confirmation' })
  } catch (error) {
    notifyError(t('subscriptions.resume.failed'))
  } finally {
    isLoadingAction.value = false
    showPreviewDialog.value = false
  }
}

// --- 5. Lifecycle ---
onMounted(() => {
  init()
})

</script>

<template>
  <q-page class="q-pa-md  ">
    <!-- Checkout View -->
    <div v-show="showCheckout">
      <div class="row items-center q-mb-md">
        <q-btn
          flat
          dense
          color="primary"
          icon="mdi-arrow-left"
          :label="$t('common.back')"
          @click="() => showCheckout = false"
        />
      </div>

      <div class="checkout-container" />
    </div>
    <div v-show="!showCheckout">
      <div class="q-gutter-y-md">
        <div class="q-mb-md">
          <div class="text-h5 text-weight-bold">
            {{ $t('subscriptions.resume.title') }}
          </div>
          <div class="text-subtitle2 text-grey-7">
            {{ $t('subscriptions.resume.subtitle') }}
          </div>
        </div>
        <div v-if="pausedSubscription">
          <!-- Paused Plan -->
          <q-card class="q-mb-sm">
            <q-card-section class="q-pa-md">
              <div class="row items-center justify-between q-mb-sm">
                <div>
                  <div class="text-h6">
                    {{ pausedSubscription?.product_name }}
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Payment Method -->
          <q-card class="q-mb-sm">
            <q-card-section class="q-pa-md">
              <div class="row items-center justify-between">
                <div class="text-h6">
                  {{ $t('subscriptions.manage.paymentMethod') }}
                </div>
                <q-icon
                  name="mdi-credit-card-outline"
                  size="1.5em"
                />
              </div>

              <div class="row items-center justify-between no-wrap">
                <div class="row items-center q-gutter-x-sm">
                  <div v-if="pausedSubscription.card_last4">
                    <div class="text-weight-medium text-capitalize">
                      {{ pausedSubscription.card_brand }} **** {{ pausedSubscription.card_last4 }}
                    </div>
                    <div class="text-caption text-grey-7">
                      {{ $t('subscriptions.manage.cardExpires') }} {{ pausedSubscription.card_exp_month }}/{{ pausedSubscription.card_exp_year }}
                    </div>
                  </div>
                  <div v-else>
                    <div class="text-weight-medium">
                      {{ $t('subscriptions.manage.savedPaymentMethod') }}
                    </div>
                    <div class="text-caption text-grey-7">
                      {{ $t('subscriptions.manage.secureBilling') }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="row justify-end q-mt-md">
                <q-btn
                  outline
                  color="primary"
                  :loading="isLoadingAction"
                  @click="onUpdatePaymentMethod"
                >
                  {{ $t('subscriptions.manage.updateCard') }}
                </q-btn>
              </div>
            </q-card-section>
          </q-card>

          <!-- Resume subscription -->
          <q-card class="q-mb-sm">
            <q-card-section class="q-pa-md">
              <div class="text-h6">
                {{ $t('subscriptions.manage.resumeSubscription') }}
              </div>
              <div class="text-caption text-grey-7">
                {{ $t('subscriptions.manage.resumeSubscriptionHint') }}
              </div>
              <div class="row justify-end q-mt-md">
                <q-btn
                  outline
                  color="primary"
                  :loading="isLoadingAction"
                  @click="onResumeSubscription"
                >
                  {{ $t('subscriptions.manage.resumeSubscriptionButton') }}
                </q-btn>
              </div>
            </q-card-section>
          </q-card>
        </div>
      </div>
      <q-dialog
        v-model="showPreviewDialog"
        persistent
      >
        <q-card style="width: 400px; max-width: 90vw;">
          <q-card-section class="bg-primary text-white">
            <div class="text-h6 text-weight-bold">
              {{ $t('subscriptions.resume.title') }}
            </div>
          </q-card-section>

          <q-card-section class="q-pt-md">
            <q-list dense>
              <q-item>
                <q-item-section class="text-grey-8">
                  {{ $t('subscription.resume.subtotal') }}
                </q-item-section>
                <q-item-section side>
                  {{ formatCurrency(previewData.recurring_transaction_details.totals.subtotal) }}
                </q-item-section>
              </q-item>

              <q-item v-if="parseFloat(previewData.recurring_transaction_details.totals.discount) > 0">
                <q-item-section class="text-positive">
                  {{ $t('subscriptions.discount') }}
                </q-item-section>
                <q-item-section
                  side
                  class="text-positive text-weight-bold"
                >
                  - {{ formatCurrency(previewData.recurring_transaction_details.totals.discount) }}
                </q-item-section>
              </q-item>

              <q-item v-if="parseFloat(previewData.recurring_transaction_details.totals.tax) > 0">
                <q-item-section class="text-grey-8">
                  {{ $t('subscriptions.taxRate') }}
                </q-item-section>
                <q-item-section side>
                  {{ formatCurrency(previewData.recurring_transaction_details.totals.tax) }}
                </q-item-section>
              </q-item>

              <q-separator class="q-my-md" />

              <q-item>
                <q-item-section>
                  <div class="text-subtitle1 text-weight-bold text-grey-9">
                    {{ $t('subscriptions.total') }}
                  </div>
                </q-item-section>
                <q-item-section
                  side
                  class="text-h6 text-weight-bolder text-primary"
                >
                  {{ formatCurrency(previewData.recurring_transaction_details.totals.grand_total) }}
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>

          <q-card-actions
            align="right"
            class="q-pa-md"
          >
            <q-btn
              v-close-popup
              flat
              :label="$t('common.cancel') "
              color="grey-8"
            />
            <q-btn
              unelevated
              :label="$t('common.confirm') "
              color="primary"
              class="text-weight-bold q-px-md"
              @click="handleConfirmResume"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

